import React from 'react';
import once from 'lodash.once';
import PropTypes from 'prop-types';
import classNames from 'classnames'

import { MESO_ALTER_CLASS, CUSTOM_TABLET_FIGURE_MAX_WIDTH, CUSTOM_TABLET_FIGURE_MIN_WIDTH, HOUSE_AD_CAMPAIN_IDS, DESKTOP_BREAKPOINT, MESO_AD_EVENTS, MESO_SLOT_STATUSES, MOBILE_BREAKPOINT, CUSTOM_X_LARGE_FIGURE_MAX_WIDTH} from '../../constants'
import {Context} from '../MesoAdsContext/MesoAdsContext';
import {Observer} from '../Observer/Observer';
import {Placeholder} from '../Placeholder/Placeholder';
import {ErrorBoundary} from '../ErrorBoundary/ErrorBoundary';
import {
    emitAdView,
    emitAdFailed,
    emitAdSucceeded
} from './events';
import {isconsentGiven} from "../../utils/consent";

import './MesoAds.less';

const isMobileBreakpoint = () => {
    return window.innerWidth < DESKTOP_BREAKPOINT;
};

const isTabletViewBreakpoint = () => {
    return window.innerWidth >= CUSTOM_TABLET_FIGURE_MIN_WIDTH && window.innerWidth <= CUSTOM_TABLET_FIGURE_MAX_WIDTH;
};
class MesoSlot extends React.Component {
    constructor(props) {
        super(props);
        this.onMesoAd = this.onMesoAd.bind(this);
        this.onError = this.onError.bind(this);
        this.onMessageReceived = this.onMessageReceived.bind(this);
        this.onVisibleOnce = once(this.onVisible.bind(this));

        this.mesoDisplayAdRef = React.createRef();
        this.state = {
            status: MESO_SLOT_STATUSES.LOADING
        };
        window.addEventListener('message', this.onMessageReceived);
    }

    onMessageReceived(event) {
        if (event.data && event.data.type === 'meso-fluid-resize') {
            this.props.onAdLoadUIReady && this.props.onAdLoadUIReady();
        }
    }

    componentDidMount() {
        this.props.onAdLoading && this.props.onAdLoading();
        this.mesoDisplayAdRef.current.addEventListener("mesoAd", this.onMesoAd);

        this.handleCookiePolicyRestriction();
    }

    componentWillUnmount() {
        this.mesoDisplayAdRef.current.removeEventListener("mesoAd", this.onMesoAd);
        window.removeEventListener('AD_BLOCK_STATUS', this.props.onAdLoadBlocked);
        window.removeEventListener('message', this.onMessageReceived)
    }

    handleCookiePolicyRestriction () {
        if (this.props.enableCookiePolicyRestriction && !isconsentGiven()) {
                this.props.onAdLoadFailed && this.props.onAdLoadFailed();
        } else {
            this.props.fireReportExposureEvent && this.props.fireReportExposureEvent();
        }
    }

    onMesoAd(event) {
        const {props: {gaiaId, slot, onAdLoadSuccess, onAdLoadFailed}} = this;

        const {code, uci, src, campaignId} = event.detail;

        let adType = 'paid ad';
        const adId = campaignId;

        if(adId && HOUSE_AD_CAMPAIN_IDS.includes(String(adId))) {
            adType = 'house ad';
        }

        const newState = {
            adUci: uci,
            adSrc: src,
            adType,
            adId
        };

        if (MESO_AD_EVENTS.PASS === code) {
            emitAdSucceeded({gaiaId, adUci: uci, adSrc: src, adPlacement: slot, adId, adType});
            newState.status = MESO_SLOT_STATUSES.LOADED;
            onAdLoadSuccess && onAdLoadSuccess();

            this.handleMesoNativeBreakPoint();

        } else if (MESO_AD_EVENTS.FAIL === code || MESO_AD_EVENTS.EMPTY === code) {
            emitAdFailed({failedReason: code, gaiaId, adUci: uci, adSrc: src, adPlacement: slot})
            newState.status = MESO_SLOT_STATUSES.FAILED;
            onAdLoadFailed && onAdLoadFailed();
        }

        this.setState(newState);

        window.dispatchEvent(new CustomEvent('mesoAdunitState', { detail: newState}));
    }

    handleMesoNativeBreakPoint() {
        if (this.props.uci === 'BRL') {
            this.postBRLBreakpointToIframe();

            window.onresize = () => this.postBRLBreakpointToIframe();
            return;
        }

        if (this.props.uci === 'NMC1') {
            this.postMarqueeBreakpointToIframe();

            window.onresize = () => this.postMarqueeBreakpointToIframe();
        }

    }

    postBRLBreakpointToIframe() {
        const isMobile = isMobileBreakpoint();
        const isTablet = isTabletViewBreakpoint();

        const details = isMobile ? { add: 'mobile', remove: 'desktop small-figure-image' } :
            (isTablet ? { add: 'desktop small-figure-image', remove: 'mobile' } : { add: 'desktop', remove: 'mobile small-figure-image' });

        this.postBreakpointMessage(details);

    }

    postMarqueeBreakpointToIframe() {
        const isXS = window.innerWidth < MOBILE_BREAKPOINT;
        const isSM = window.innerWidth < DESKTOP_BREAKPOINT;
        const isLarge = window.innerWidth < CUSTOM_X_LARGE_FIGURE_MAX_WIDTH;

        const details = isXS ? { add: 'x-small', remove: 'small large x-large' } :
            (isSM ? { add: 'small', remove: 'x-small large x-large' } : (isLarge ? { add: 'large', remove: 'x-small small x-large' } : { add: 'x-large', remove: 'x-small small large' } ));

        this.postBreakpointMessage(details);
    }

    postBreakpointMessage(details) {
        if(this.state.breakpointClasses && this.state.breakpointClasses === details.add) {
            return;
        }

        this.setState({breakpointClasses : details.add})
        const mesoNative = this.mesoDisplayAdRef.current.shadowRoot;
        const mesoGPTFluid = mesoNative.firstElementChild;
        const mesoGPT = mesoGPTFluid.shadowRoot.firstElementChild;
        const iframe = mesoGPT.shadowRoot.children[0].getElementsByTagName('iframe')[0];

        iframe.contentWindow.postMessage({
            type: MESO_ALTER_CLASS, details
        }, '*');
    }

    get loadSuccess() {
        return this.state.status === MESO_SLOT_STATUSES.LOADED;
    }

    getContainerClasses() {
        const {slot, strategy} = this.props;
        const slotClass = `meso-ad-${slot}`;
        const classes = {
            [slotClass]: true
        };

        if (strategy === 'expand') {
            classes[slotClass] = this.loadSuccess;
            classes['meso-ad-loading'] = !this.loadSuccess && this.state.status !== MESO_SLOT_STATUSES.FAILED;
            classes['meso-ad-failed'] = this.state.status === MESO_SLOT_STATUSES.FAILED;
        }
        return classNames('meso-ad-container', classes);
    }

    onError() {
        const {slot, gaiaId} = this.props;
        const {adUci, adSrc} = this.state;

        emitAdFailed({
            failedReason: MESO_SLOT_STATUSES.RENDERING_FAILURE,
            adPlacement: slot,
            gaiaId,
            adUci,
            adSrc
        });
    }

    onVisible() {
        const {slot, gaiaId} = this.props;
        const {adUci, adSrc, adType, adId} = this.state;

        emitAdView({
            adPlacement: slot,
            gaiaId,
            adUci,
            adSrc,
            adType,
            adId

        });
    }

    render() {
        const {slot, uci, style = {}, nativeType, renderPlaceholder} = this.props;
        const slotClasses = this.getContainerClasses();
        return (
            <ErrorBoundary onError={this.onError}>
                <div className={slotClasses} style={style}>
                    {renderPlaceholder && <Placeholder extraClasses="meso-ad-placeholder"/>}
                    {nativeType ?
                         <meso-native
                            ref={this.mesoDisplayAdRef}
                            uci={uci}
                            adslot={slot}
                            nativetype={nativeType}
                            isfluid="true"
                            timeout={4000} />
                         :
                         <meso-display-ad
                            ref={this.mesoDisplayAdRef}
                            uci={slot}
                            slot={slot} />
                    }
                            {this.loadSuccess && <Observer onVisible={this.onVisibleOnce}>
                            {(ref) => <div ref={ref} />}
                        </Observer>}
                </div>
            </ErrorBoundary>
        );
    }
}

export const MesoAds = function ({slot, uci, nativeType, strategy, onAdLoadSuccess, onAdLoadUIReady, onAdLoadFailed, onAdLoadBlocked, onAdLoading, style, renderPlaceholder, enableCookiePolicyRestriction, fireReportExposureEvent}) {
    return (
        <Context.Consumer>
            {({allowAds, gaiaId}) => allowAds ?
                <MesoSlot
                    gaiaId={gaiaId}
                    slot={slot}
                    uci={uci}
                    nativeType={nativeType}
                    strategy={strategy}
                    onAdLoadSuccess={onAdLoadSuccess}
                    onAdLoadUIReady={onAdLoadUIReady}
                    onAdLoadFailed={onAdLoadFailed}
                    onAdLoadBlocked={onAdLoadBlocked}
                    onAdLoading={onAdLoading}
                    style={style}
                    renderPlaceholder={renderPlaceholder}
                    enableCookiePolicyRestriction={enableCookiePolicyRestriction}
                    fireReportExposureEvent={fireReportExposureEvent}
                />
                : null
            }
        </Context.Consumer>
    );
};

MesoAds.defaultProps = {
    strategy: 'expand',
    renderPlaceholder: true
};

MesoAds.propTypes = {
    slot: PropTypes.string,
    uci: PropTypes.string,
    nativeType: PropTypes.string,
    strategy: PropTypes.oneOf([
        'expand', 'collapse'
    ]),
    onAdLoadSuccess: PropTypes.func,
    onAdLoadFailed: PropTypes.func,
    onAdLoadBlocked: PropTypes.func,
    onAdLoadUIReady: PropTypes.func,
    onAdLoading: PropTypes.func,
    style: PropTypes.object,
    renderPlaceholder: PropTypes.bool,
    // call onAdLoadFailed if the GDPR policy not accepted, and call fireReportExposureEvent when the GDPR is accepted.
    enableCookiePolicyRestriction: PropTypes.bool,
    // this custom reporting callback will be executed directly if we did not pass enableCookiePolicyRestriction
    fireReportExposureEvent: PropTypes.func
};
